import { BarChart, Box, Card, Text } from '@factorialco/deprecated-design-system'
import { animated, useSpring } from '@react-spring/web'
import moment from 'moment'

import DraggableNode from 'modules/core/components/DraggableNode'
import useMouseCoords from 'modules/core/hooks/useMouseCoords'
import { dateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import magnitude from 'modules/insights/lib/magnitude'
import type Retention from 'modules/insights/lib/metrics/retention'

import BlankSlate from '../BlankSlate'
import ChartTooltip from '../ChartTooltip'
import TooltipLegend from '../TooltipLegend'

type Props = {
  retention: Retention
}

const WidgetRetention = ({ retention }: Props) => {
  const emptyResult = retention.isEmpty()
  const data = retention.generateByRanges()
  const coordinates = useMouseCoords()
  let firstDate = moment()
  let lastDate = moment()
  let value = 0

  const max = Math.max(...data.map(({ value: rententionValue }) => rententionValue))
  const magnitudeY = max < 100 ? magnitude(max) : 100

  if (data[0]) {
    firstDate = stringToDate(data[0].startDate, 'backend')
  }

  if (data[data.length - 1]) {
    value = data[data.length - 1]!.value
    lastDate = stringToDate(data[data.length - 1]!.endDate, 'backend')
  }

  const spring = useSpring({ value })
  const description = `${dateToString(firstDate, 'ui_abbreviated_month_year')} - ${dateToString(
    lastDate,
    'ui_abbreviated_month_year'
  )}`

  return (
    <Card>
      <Card.Title tooltip={i18n.t('insights.widgets.retention.tooltip')}>
        {i18n.t('insights.widgets.retention.title')}
      </Card.Title>
      <Card.Description>{description}</Card.Description>
      <Card.Content>
        <Box paddingY="s16">
          <Box flexDirection="row" alignItems="baseline" paddingTop="s4">
            <Text size="600" weight="semibold" color="grey1000">
              <animated.div>
                {spring.value.to((currentValue) => {
                  return Number(currentValue.toFixed(1))
                })}
              </animated.div>
            </Text>
            <Text size="500" weight="semibold" color="grey1000">
              %
            </Text>
          </Box>
          <Text size="200" weight="normal" color="grey1000">
            {dateToString(lastDate, 'ui_month_year')}
          </Text>
        </Box>
        {emptyResult ? (
          <BlankSlate />
        ) : (
          <Box height="s140" width="full">
            <BarChart
              axisBottom={{
                format: (label: string) =>
                  stringToDate(label, 'backend').format('MMMM').charAt(0).toUpperCase(),
              }}
              tooltip={({ label, value: retentionValue }) => {
                const title = dateToString(
                  stringToDate(label as string, 'backend'),
                  'ui_month_year'
                )

                return (
                  <DraggableNode coordinates={coordinates}>
                    <ChartTooltip title={i18n.t('insights.widgets.retention.title')}>
                      <TooltipLegend
                        color={'viridian1000'}
                        name={title}
                        value={`${retentionValue}%`}
                      />
                    </ChartTooltip>
                  </DraggableNode>
                )
              }}
              colors={['viridian1000']}
              margin={{ top: 15, bottom: 25, left: 30, right: -20 }}
              maxValue={magnitudeY}
              keys={['value']}
              indexBy="startDate"
              data={data}
            />
          </Box>
        )}
      </Card.Content>
    </Card>
  )
}

export default WidgetRetention
